import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import {
  ApprovalDto,
  ApprovalDetailDto,
  DetectedFileInfo,
  DownloadHistoryInfo,
  FileDetailsInfo,
  FileInfoDto,
  ApprovalType,
} from './approvalApi';
import { UseYN } from '../infra';
import { toBoolean, toUseYN } from 'src/shared/utils/infra';
import { toLocalDateTimeStr } from 'src/shared/utils/localDateTime';

const FXM_API_URL = config.servers.fxm.url;
export interface EncMailApprovalDto extends ApprovalDto {
  approvalNo: string;
  submission: string;
  subject: string;
  content: string;
  contentType: 'text' | 'html';
  sendSeparate: UseYN;
  priority: UseYN;
  isManualSend: UseYN;
  isAutoSend: UseYN;
  to: string[];
  cc: string[];
}
export enum DocDownloadAction {
  DOWNLOADDECRYPT = 'DOWNLOADDECRYPT',
  DOWNLOADORIGIN = 'DOWNLOADORIGIN', //(복호화 다운, 원본 다운
}
export const docDownloadActionRecord: Record<DocDownloadAction, string> = {
  [DocDownloadAction.DOWNLOADDECRYPT]: '복호화 다운',
  [DocDownloadAction.DOWNLOADORIGIN]: '원본 다운',
};

export type SendMailStatus = 'ERR' | 'OK' | 'PREPARE';
export interface SendMailsDto {
  mailNo: string; //메일 전송 고유 번호 (필수 값), 최대 64자
  sendStatus: SendMailStatus; // 단체 혹은 개인별 메일 전송 상태, ERR(에러) | OK(성공) | PREPARE(대기)
  approvalNo: string; //결재 고유 번호
  updatedTs: number; //전송 시간
  isZip: UseYN; //zip 전달 유무 (Y zip 전달, N 개별 파일 전달)
  to: string; // 메일 받는 사람. (List 가 아니라 string 으로 ; 로 구분됨, 개별 전달이 아닐 시 a;b;c 와 같이 됨)
}

export interface EncMailApprovalDetailDto extends ApprovalDetailDto {
  submission: string;
  fileDetails: FileDetailsInfo[];
  downloadHistory: DownloadHistoryInfo[];
  sendMails: SendMailsDto[];
  sendStatus: SendMailStatus;
  subject: string; // 메일제목
  content: string; // 메일 내용
  contentType: 'text' | 'html'; //
  sendSeparate: UseYN; // 각각 전송
  priority: UseYN; // 중요 메일 여부
  isManualSend: UseYN; // 수동 전송
  isAutoSend: UseYN; // 자동 전송
  to: string[];
  cc: string[];
  startTs: number;
  endTs: number;
  detectedFiles?: DetectedFileInfo[];
}
export type EncMailApprovalReqDto = EncMailApprovalDto;
export interface ApplyGenKeyDto {
  approvalNo: string;
}
export interface SendMailsEntity extends Omit<SendMailsDto, 'isZip'> {
  isZip: boolean; //zip 전달 유무 (Y zip 전달, N 개별 파일 전달)
}

export interface EncMailApprovalDetailEntity
  extends Omit<
    EncMailApprovalDetailDto,
    // 'code' |
    'sendMails' | 'sendSeparate' | 'priority' | 'isManualSend' | 'isAutoSend'
  > {
  sendMails: SendMailsEntity[];
  sendSeparate: boolean;
  priority: boolean;
  isManualSend: boolean;
  isAutoSend: boolean;
  applyDateStr: string;
  updatedTsStr: string;
}
export const toEncMailApprovalDetailDtoToEntity = (
  dto: EncMailApprovalDetailDto
): EncMailApprovalDetailEntity => {
  return {
    ...dto,
    progressApprovers: dto.progressApprovers === null ? [] : dto.progressApprovers,
    completeApprovers: dto.completeApprovers === null ? [] : dto.completeApprovers,
    sendMails: dto.sendMails.map(
      (mail): SendMailsEntity => ({ ...mail, isZip: mail.isZip === UseYN.Y })
    ),
    sendSeparate: toBoolean(dto.sendSeparate),
    priority: toBoolean(dto.priority),
    isManualSend: toBoolean(dto.isManualSend),
    isAutoSend: toBoolean(dto.isAutoSend),
    applyDateStr: dto.applyDate > 0 ? toLocalDateTimeStr(dto.applyDate) : '',
    updatedTsStr: dto.updatedTs > 0 ? toLocalDateTimeStr(dto.updatedTs) : '',
  };
};
export const toEncMailApprovalDetailEntityToDto = (
  entity: EncMailApprovalDetailEntity
): EncMailApprovalDetailDto => {
  return {
    ...entity,
    code: entity.code,
    progressApprovers: entity.progressApprovers === null ? [] : entity.progressApprovers,
    completeApprovers: entity.completeApprovers === null ? [] : entity.completeApprovers,
    sendMails: entity.sendMails.map(
      (mail): SendMailsDto => ({ ...mail, isZip: toUseYN(mail.isZip) })
    ),
    sendSeparate: toUseYN(entity.sendSeparate),
    priority: toUseYN(entity.priority),
    isManualSend: toUseYN(entity.isManualSend),
    isAutoSend: toUseYN(entity.isAutoSend),
  };
};
export interface EncMailMailPreviewReqDto {
  userName: string;
  userId: string;
  subject: string;
  content: string;
  detailEntity: EncMailApprovalDetailEntity | null;
  contentType?: 'text' | 'html';
}

interface EncMailApprovalApi {
  find(approvalNo: string): Promise<EncMailApprovalDetailEntity>; // 사용자 > 내 신청함 > 결재 완료 이외 부분에서 사용하는 API
  find2(approvalNo: string): Promise<EncMailApprovalDetailEntity>; // 사용자 > 내 신청함 > 결재완료에서 사용 하는 API
  getGenKey(): Promise<ApplyGenKeyDto>;
  fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]>;
  deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto>;
  deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]>;
  apply(reqDto: EncMailApprovalReqDto): Promise<EncMailApprovalDetailDto>;
  cancel(approvalNo: string): Promise<boolean>;
  send(approvalNo: string): Promise<boolean>;
  preview(reqDto: EncMailMailPreviewReqDto): Promise<string>;
}
class EncMailApprovalServerApi implements EncMailApprovalApi {
  async find(approvalNo: string): Promise<EncMailApprovalDetailEntity> {
    const resp = await axios.get<EncMailApprovalDetailDto>(
      `${FXM_API_URL}/api/encmail/find/${approvalNo}`
    );
    return toEncMailApprovalDetailDtoToEntity(resp.data);
  }
  async find2(approvalNo: string): Promise<EncMailApprovalDetailEntity> {
    const resp = await axios.get<EncMailApprovalDetailDto>(
      `${FXM_API_URL}/api/encmail/find2/${approvalNo}`
    );
    return toEncMailApprovalDetailDtoToEntity(resp.data);
  }
  async getGenKey(): Promise<ApplyGenKeyDto> {
    const resp = await axios.get<ApplyGenKeyDto>(`${FXM_API_URL}/api/genkey`);
    return resp.data;
  }
  async fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]> {
    const form = new FormData();
    form.append('approvalType', 'ENCMAIL');
    form.append('approvalNo', approvalNo);
    form.append('code', code);
    files.map((file) => form.append('files', file));

    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/encmail/upload`,
      form,
      progressConfig
    );

    return resp.data;
  }

  async deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto> {
    const resp = await axios.get<FileInfoDto>(
      `${FXM_API_URL}/api/encmail/delete/one/${approvalNo}/${fileUuid}`
    );
    return resp.data;
  }
  async deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]> {
    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/encmail/delete/many/${approvalNo}`,
      fileUuids
    );
    return resp.data;
  }

  async apply(reqDto: EncMailApprovalReqDto): Promise<EncMailApprovalDetailDto> {
    const resp = await axios.post<EncMailApprovalDetailDto>(
      `${FXM_API_URL}/api/encmail/apply`,
      reqDto
    );
    return resp.data;
  }

  async cancel(approvalNo: string): Promise<boolean> {
    await axios.get<EncMailApprovalDetailDto>(`${FXM_API_URL}/api/encmail/cancel/${approvalNo}`);
    return true;
  }

  async send(approvalNo: string): Promise<boolean> {
    await axios.get<void>(`${FXM_API_URL}/api/encmail/send/${approvalNo}`);
    return true;
  }
  async preview(reqDto: EncMailMailPreviewReqDto): Promise<string> {
    const res = await axios.post<string>(`${FXM_API_URL}/api/mail/preview`, {
      template: 'fxm_encmail_send', //fxm_encmail_send 고정(FXMv2.3)
      userName: reqDto.userName,
      userId: reqDto.userId,
      subject: reqDto.subject,
      contentType: reqDto.contentType,
      content: reqDto.content,
      approval: reqDto.detailEntity ? toEncMailApprovalDetailEntityToDto(reqDto.detailEntity) : '',
    });
    return res.data;
  }
}
const encMailApprovalApi = new EncMailApprovalServerApi();
export default encMailApprovalApi;
